// useOnboardingTelemetry — fires once-per-event onboarding signals on
// the global window event-bus. Listeners (analytics-loaders) opt in
// without coupling to React state.
//
// Events fired:
//   fw-onboarding-mobile-first-visit  — first visit on a mobile UA
//   fw-onboarding-completed           — Welcome wizard finished
//   fw-onboarding-first-save          — user persists their first workflow
//
// Each event is suppressed after first firing via a localStorage flag
// so analytics aren't spammed and the toast doesn't re-pester.

import { useEffect } from 'react';
import { toast } from '@/hooks/use-toast';

const FLAGS = {
  mobileFirstVisit: 'fw.telemetry.mobile-first-visit',
  onboardingCompleted: 'fw.telemetry.onboarding-completed',
  firstSave: 'fw.telemetry.first-save',
} as const;

function once(flagKey: string, fire: () => void): void {
  try {
    if (localStorage.getItem(flagKey) !== null) return;
    localStorage.setItem(flagKey, new Date().toISOString());
  } catch {
    // storage unavailable — fall through (might fire twice, acceptable)
  }
  fire();
}

function dispatch(eventName: string, detail?: Record<string, unknown>) {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(eventName, { detail }));
}

/**
 * Single mount-point at the app root. Detects mobile + fires the
 * once-per-device toast + telemetry event.
 */
export function useOnboardingTelemetryMount({
  isMobile,
}: {
  isMobile: boolean;
}): void {
  useEffect(() => {
    if (!isMobile) return;
    once(FLAGS.mobileFirstVisit, () => {
      dispatch('fw-onboarding-mobile-first-visit');
      toast({
        title: 'Bäst i desktop',
        description:
          'Flow Weaver är optimerat för stora skärmar. Mobile-läget fungerar men vissa funktioner är begränsade.',
      });
    });
  }, [isMobile]);
}

/** Call after the user clicks "Done" on the Welcome/Wizard. */
export function fireOnboardingCompleted(payload: { templateId?: string }) {
  once(FLAGS.onboardingCompleted, () => {
    dispatch('fw-onboarding-completed', payload);
  });
}

/** Call after the user's first save (cloud or file). */
export function fireFirstSave(payload: { destination: 'cloud' | 'file' }) {
  once(FLAGS.firstSave, () => {
    dispatch('fw-onboarding-first-save', payload);
  });
}

export const ONBOARDING_TELEMETRY_FLAGS = FLAGS;
